import { useEffect, useState } from "react";
import { api } from "../redux/api/api";
import {
  cacheInitials,
  initialsFromNames,
  readCachedInitials,
  readTokenIdentity,
  unwrapProfileBody,
} from "../utils/account";

/**
 * The initials to show on the account avatar.
 *
 * The avatar used to render a single letter taken from `state.auth.user.user_name`,
 * which is only in redux for the lifetime of the login request: on reload
 * `getStoredUser()` rebuilds the user from localStorage as `{ token, access }`
 * only, so the name was gone and the avatar fell back to "?" for the rest of the
 * session. My Profile meanwhile shows two real initials because it fetches the
 * profile itself.
 *
 * So read the same source My Profile does, and show the same two letters. The
 * result is cached in localStorage, so after the first load the avatar paints
 * with the right initials immediately rather than flashing the fallback, and it
 * is cleared on login, logout and forced re-auth so one account never inherits
 * another's.
 *
 * A failure here is deliberately silent: an avatar is not worth a toast, and the
 * profile screen already reports the same request's errors properly.
 */
export const useAccountInitials = (fallback: string) => {
  const [initials, setInitials] = useState(readCachedInitials);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const identity = readTokenIdentity();
        const res = await api.post("/users/getProfile", { ...identity, user: identity });
        const body = res.data;
        // Both envelopes: /users/* answers { ok, data }, the main API { status, data }.
        if (body?.ok === false || body?.status === false) return;

        const profile = unwrapProfileBody(body);
        const next = initialsFromNames(profile?.firstName, profile?.lastName);
        if (!next || cancelled) return;

        cacheInitials(next);
        setInitials(next);
      } catch {
        // Keep whatever is cached, or the caller's fallback.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return initials || fallback;
};

export default useAccountInitials;
